import { useStaticCallback } from '@sparemin/blockhead';
import cn from 'classnames';
import * as playerjs from 'player.js';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { isUndefined } from 'underscore';
import { useVideoEditorPlaybackTimeContext } from 'containers/VideoEditor/VideoEditorPlaybackTimeContext';
import { getAspectRatioName } from 'utils/aspect-ratio';
import { block } from './utils';

export interface EmbedPlayerProps {
  aspectRatio: number;
  className?: string;
  dragFixOverlay?: boolean;
  lastPlaybackPos?: number;
  onDestroy?: (pos: number) => void;
  onEnded?: playerjs.EventHandler<'ended'>;
  onPause?: playerjs.EventHandler<'pause'>;
  onPlay?: playerjs.EventHandler<'play'>;
  onSeeked?: (pos: number) => void;
  onTimeupdate?: playerjs.EventHandler<'timeupdate'>;
  /**
   * Called when the player is ready
   */
  onReady?: () => void;
  /**
   * Called when the iframe loads
   */
  onLoad?: (element: HTMLIFrameElement) => void;
  playing?: boolean;
  src: string;
  volume?: number;
}

export type EmbedPlayerInstance = {
  getCurrentTime: () => number;
  getDuration: () => number;
  setCurrentTime: (sec: number) => void;
};

const EmbedPlayer = React.memo(
  React.forwardRef<EmbedPlayerInstance, EmbedPlayerProps>(
    (
      {
        aspectRatio,
        className,
        dragFixOverlay,
        lastPlaybackPos,
        onDestroy,
        onEnded,
        onLoad,
        onPause,
        onPlay,
        onSeeked,
        onTimeupdate,
        onReady,
        playing,
        src,
        volume,
      },
      ref,
    ) => {
      const [isReady, setIsReady] = useState(false);

      const iframe = useRef<HTMLIFrameElement>();
      const player = useRef<playerjs.Player>();
      const currentTime = useRef<number>();
      const duration = useRef<number>();

      const ctx = useVideoEditorPlaybackTimeContext();
      const {
        positionSec: videoEditorPlaybackTimeSec,
        seekToSec: videoEditorSeekSec,
      } = ctx;

      const aspectRatioName = getAspectRatioName(aspectRatio);
      const seekPosition = lastPlaybackPos ?? videoEditorSeekSec;

      const staticOnDestroy = useStaticCallback(onDestroy);
      const staticOnEnded = useStaticCallback(onEnded);
      const staticOnPause = useStaticCallback(onPause);
      const staticOnPlay = useStaticCallback(onPlay);

      // it's important to the effects below that this function remains static
      const ifPlayerIsReady = useCallback(
        <T extends unknown>(fn: (player: playerjs.Player) => T) => {
          if (player.current && isReady) {
            return fn(player.current);
          }
          return undefined;
        },
        [isReady],
      );

      // note that the following effects all look for a single condition, however
      // each effect should also run when isReady changes, since there might be
      // a race condition between performing the action in the effect and the player
      // becoming ready

      // this effect should only update the active playback state when the `playing`
      // prop changes.
      useEffect(() => {
        if (!isUndefined(playing)) {
          ifPlayerIsReady(p => {
            p.getPaused(isPaused => {
              if (playing && isPaused) {
                p.play();
              } else if (!playing && !isPaused) {
                p.pause();
              }
            });
          });
        }
      }, [ifPlayerIsReady, playing]);

      // this effect should only update the active playback state when the
      // seek position changes.
      useEffect(() => {
        if (!isUndefined(seekPosition)) {
          ifPlayerIsReady(p => {
            p.setCurrentTime(seekPosition);
          });
        }
      }, [ifPlayerIsReady, seekPosition]);

      // this effect should only update the active playback state when the `volume`
      // prop changes.
      useEffect(() => {
        if (!isUndefined(volume)) {
          ifPlayerIsReady(p => {
            p.setVolume(volume);
          });
        }
      }, [ifPlayerIsReady, volume]);

      const staticHandleTimeupdate = useStaticCallback(
        (...args: Parameters<EmbedPlayerProps['onTimeupdate']>) => {
          currentTime.current = args[0].seconds;
          onTimeupdate?.(...args);
        },
      );

      const staticHandleSeeked = useStaticCallback(() => {
        ifPlayerIsReady(p => {
          p.getCurrentTime(pos => onSeeked(pos));
        });
      });

      const staticHandleReady = useStaticCallback(() => {
        player.current.getDuration(d => {
          duration.current = d;

          // set isReady early, that way any handlers that fire and use ifPlayerIsReady
          // won't get blocked by the player not being ready yet
          setIsReady(true);

          if (!isUndefined(videoEditorPlaybackTimeSec)) {
            player.current.setCurrentTime(videoEditorPlaybackTimeSec);
          }

          if (playing) {
            player.current.play();
          }

          onReady();
        });
      });

      const attachEventListeners = useCallback(() => {
        if (player.current) {
          // NB: all handlers attached here must be stable.  attachEventListeners
          // gets called when the new player is created.  if any callbacks change
          // after that, they won't get attached and so will never get called
          player.current.on('ready', staticHandleReady);
          player.current.on('timeupdate', staticHandleTimeupdate);
          player.current.on('play', staticOnPlay);
          player.current.on('pause', staticOnPause);
          player.current.on('ended', staticOnEnded);
          player.current.on('seeked', staticHandleSeeked);
        }
      }, [
        staticHandleReady,
        staticHandleSeeked,
        staticHandleTimeupdate,
        staticOnEnded,
        staticOnPause,
        staticOnPlay,
      ]);

      const removeEventListeners = useCallback(() => {
        if (player.current) {
          player.current.off('timeupdate');
          player.current.off('play');
          player.current.off('pause');
          player.current.off('ended');
          player.current.off('seeked');
          player.current.off('ready');
        }
      }, []);

      const handleIFrameLoad = useCallback(() => {
        player.current = new playerjs.Player(iframe.current);
        attachEventListeners();
        player.current.setVolume(volume);
        onLoad?.(iframe.current);
      }, [attachEventListeners, onLoad, volume]);

      const handleIFrameRef = useCallback(
        (el: HTMLIFrameElement | null) => {
          if (!el && iframe.current) {
            removeEventListeners();
            staticOnDestroy(currentTime.current);
          }
          iframe.current = el;
        },
        [removeEventListeners, staticOnDestroy],
      );

      useImperativeHandle(
        ref,
        () => ({
          getCurrentTime() {
            return currentTime.current;
          },
          getDuration() {
            return duration.current;
          },
          setCurrentTime(sec) {
            player.current?.setCurrentTime(sec);
          },
        }),
        [],
      );

      return (
        <div
          className={cn(
            block('player', {
              [aspectRatioName]: !isUndefined(aspectRatioName),
            }),
            className,
          )}
        >
          <iframe
            allowFullScreen
            allow="autoplay"
            className={block('iframe')}
            ref={handleIFrameRef}
            onLoad={handleIFrameLoad}
            scrolling="no"
            src={src}
            style={dragFixOverlay ? { pointerEvents: 'none' } : undefined}
          />
        </div>
      );
    },
  ),
  (prevProps, props) =>
    prevProps.dragFixOverlay === props.dragFixOverlay &&
    prevProps.playing === props.playing,
);

export default EmbedPlayer;
